import axios from "axios";

// Create an Axios instance
const axiosInstance = axios.create({
  baseURL: "https://admin.zeinfoods.com/api", // Replace with your API base URL
  headers: {
    "Content-Type": "application/json",
    "Access-Control-Allow-Origin": "*",
    Authorization: `${localStorage.getItem("authToken")}` || "",
  },
});

// Request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    // Handle successful responses
    if (response.data.status === "success") {
      console.log(response.data.message || "Operation successful");
    }
    return response;
  },
  (error) => {
    // Handle errors based on the backend response structure
    if (error.response) {
      const { data } = error.response;

      if (data.status === "error" && data.errors) {
        const errorMessages = Object.entries(data.errors)
          .map(([field, messages]) => `${field}: ${messages.join(", ")}`)
          .join("\n");
        console.error("Validation Errors:", errorMessages);
      } else {
        console.error(
          "Error response:",
          data.message || "An unknown error occurred"
        );
      }
    } else if (error.request) {
      console.error("No response received:", error.request);
    } else {
      console.error("Error:", error.message);
    }

    return Promise.reject(error);
  }
);

// In-memory cache for GET requests to avoid repeated calls when navigating
const getCache = new Map();

const originalGet = axiosInstance.get;
axiosInstance.get = async (url, config = {}) => {
  // Skip caching if explicitly requested
  if (config.skipCache) {
    return originalGet(url, config);
  }

  // Create a unique key based on URL and query params
  const cacheKey = url + (config.params ? JSON.stringify(config.params) : "");
  const localCacheKey = "api_cache_" + cacheKey;

  // 1. Check in-memory cache first
  if (getCache.has(cacheKey)) {
    const cachedResponse = getCache.get(cacheKey);
    // Deep clone the data to prevent mutations from affecting the cached reference
    return Promise.resolve({
      ...cachedResponse,
      data: JSON.parse(JSON.stringify(cachedResponse.data)),
    });
  }

  try {
    // 2. Make the actual request if not in memory cache
    const response = await originalGet(url, config);

    // 3. Store the response in memory cache
    getCache.set(cacheKey, response);
    
    // 4. Also store the data in localStorage for persistence across refreshes
    try {
      localStorage.setItem(localCacheKey, JSON.stringify(response.data));
    } catch (e) {
      // Ignore localStorage errors (e.g., quota exceeded)
      console.warn("Could not save to localStorage", e);
    }

    return response;
  } catch (error) {
    // 5. If the request fails (e.g., API is down or network error after refresh),
    // fallback to the data stored in localStorage if available.
    const localData = localStorage.getItem(localCacheKey);
    if (localData) {
      console.warn(`API request failed for ${url}. Using fallback data from localStorage.`);
      const parsedData = JSON.parse(localData);
      
      const fallbackResponse = {
        data: parsedData,
        status: 200,
        statusText: "OK (from Local Storage)",
        headers: {},
        config
      };

      // Store in memory cache for subsequent calls
      getCache.set(cacheKey, fallbackResponse);

      return Promise.resolve(fallbackResponse);
    }

    // If no fallback is available, throw the original error
    throw error;
  }
};

export default axiosInstance;
